import { useState, useEffect } from 'react'
import { get, post, patch, del } from '../lib/api'

const CATEGORIES = ['Travel', 'Meals', 'Accommodation', 'Supplies', 'Software', 'Training', 'Other']
const MAX_RECEIPT_BYTES = 300 * 1024 // 300 KB
const STATUS = {
  pending:  { label: 'Pending',  bg: '#FFFBEB', color: '#D97706' },
  approved: { label: 'Approved', bg: '#ECFDF5', color: '#059669' },
  rejected: { label: 'Rejected', bg: '#FEF2F2', color: '#EF4444' },
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = reject
    r.readAsDataURL(file)
  })
}

export default function ExpenseLog({ t, isEmployer }) {
  const [expenses, setExpenses] = useState([])
  const [empMap, setEmpMap] = useState({})
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ amount: '', category: 'Travel', description: '', expense_date: '', receipt_data: '', receipt_name: '' })
  const [msg, setMsg] = useState('')
  const [saving, setSaving] = useState(false)
  const [rejectingId, setRejectingId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    try {
      const r = await get('/expenses')
      setExpenses(r.expenses || [])
      if (isEmployer) {
        const u = await get('/users')
        setEmpMap(Object.fromEntries((u.users || []).map((x) => [x.id, x])))
      }
    } catch (e) { console.error(e.message) }
  }

  async function handleReceipt(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > MAX_RECEIPT_BYTES) { setMsg('❌ Receipt must be under 300 KB'); return }
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) { setMsg('❌ Only JPG, PNG, WebP or PDF'); return }
    const dataUrl = await readFileAsDataURL(file)
    setForm((f) => ({ ...f, receipt_data: dataUrl, receipt_name: file.name }))
    setMsg('')
  }

  async function submit(e) {
    e.preventDefault()
    if (!form.amount) { setMsg('❌ Enter an amount'); return }
    setSaving(true); setMsg('')
    try {
      await post('/expenses', { ...form, amount: parseFloat(form.amount) })
      setForm({ amount: '', category: 'Travel', description: '', expense_date: '', receipt_data: '', receipt_name: '' })
      setShowForm(false)
      fetchAll()
    } catch (err) { setMsg(`❌ ${err.message}`) }
    setSaving(false)
  }

  async function setStatus(exp, status) {
    try { await patch(`/expenses/${exp.id}`, { status }); fetchAll() } catch (err) { alert(err.message) }
  }
  async function confirmReject(exp) {
    try {
      await patch(`/expenses/${exp.id}`, { status: 'rejected', reviewer_comment: rejectReason.trim() || null })
      setRejectingId(null); setRejectReason(''); fetchAll()
    } catch (err) { alert(err.message) }
  }
  async function remove(id) {
    if (!window.confirm('Delete this expense?')) return
    try { await del(`/expenses/${id}`); fetchAll() } catch (err) { alert(err.message) }
  }

  const IS = { display: 'block', width: '100%', padding: '11px 14px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 14, color: t.inputText, fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none' }
  const LS = { display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }
  const card = { background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, boxShadow: t.shadow }

  const total = expenses.filter((e) => e.status === 'approved').reduce((s, e) => s + (e.amount || 0), 0)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Expense Log</h1>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>
            {isEmployer ? 'Review and approve team expenses' : 'Log your expenses and attach receipts'}
            {' · '}Approved total: <strong style={{ color: t.text }}>₹{total.toLocaleString()}</strong>
          </p>
        </div>
        {!isEmployer && (
          <button onClick={() => setShowForm((s) => !s)} style={{ padding: '10px 18px', background: 'linear-gradient(135deg,#4F46E5,#6366F1)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
            {showForm ? 'Cancel' : '+ New expense'}
          </button>
        )}
      </div>

      {!isEmployer && showForm && (
        <form onSubmit={submit} style={{ ...card, padding: '1.5rem', marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
            <div><label style={LS}>Amount (₹)</label><input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" style={IS} /></div>
            <div><label style={LS}>Category</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={IS}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
          </div>
          <div style={{ marginBottom: '1rem' }}><label style={LS}>Date</label><input type="date" max={new Date().toISOString().split('T')[0]} value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} style={IS} /></div>
          <div style={{ marginBottom: '1rem' }}><label style={LS}>Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What was this expense for?" style={{ ...IS, resize: 'vertical' }} /></div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={LS}>Receipt (image or PDF, max 300 KB)</label>
            <input type="file" accept="image/*,application/pdf" onChange={handleReceipt} style={{ fontSize: 13, color: t.text }} />
            {form.receipt_name && <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>✓ {form.receipt_name}</div>}
          </div>
          {msg && <div style={{ fontSize: 13, color: msg.startsWith('✅') ? '#059669' : '#EF4444', marginBottom: 12 }}>{msg}</div>}
          <button type="submit" disabled={saving} style={{ padding: '11px 22px', background: 'linear-gradient(135deg,#4F46E5,#6366F1)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
            {saving ? 'Submitting…' : 'Submit expense'}
          </button>
        </form>
      )}

      {expenses.length === 0 ? (
        <p style={{ color: t.textMuted }}>No expenses yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {expenses.map((ex) => {
            const st = STATUS[ex.status] || STATUS.pending
            return (
              <div key={ex.id} style={{ ...card, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: t.text }}>₹{(ex.amount || 0).toLocaleString()}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, background: '#4F46E514', color: '#4F46E5', padding: '2px 8px', borderRadius: 99 }}>{ex.category}</span>
                      {isEmployer && empMap[ex.employee_id] && <span style={{ fontSize: 12, color: t.textMuted }}>· {empMap[ex.employee_id].full_name}</span>}
                    </div>
                    {ex.description && <div style={{ fontSize: 13, color: t.textSub, marginTop: 3 }}>{ex.description}</div>}
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 3 }}>
                      {ex.expense_date || '—'}
                      {ex.receipt_data && <> · <a href={ex.receipt_data} download={ex.receipt_name || 'receipt'} style={{ color: '#4F46E5', fontWeight: 600 }}>📎 receipt</a></>}
                    </div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, background: st.bg, color: st.color, padding: '4px 12px', borderRadius: 99 }}>{st.label}</span>
                  {isEmployer && ex.status === 'pending' && rejectingId !== ex.id && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setStatus(ex, 'approved')} style={{ padding: '6px 12px', background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Approve</button>
                      <button onClick={() => { setRejectingId(ex.id); setRejectReason('') }} style={{ padding: '6px 12px', background: '#FEF2F2', color: '#EF4444', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Reject</button>
                    </div>
                  )}
                  {!isEmployer && ex.status === 'pending' && (
                    <button onClick={() => remove(ex.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: t.textMuted }}>🗑</button>
                  )}
                </div>

                {/* Rejection reason (visible to both once rejected) */}
                {ex.status === 'rejected' && ex.reviewer_comment && (
                  <div style={{ marginTop: 10, fontSize: 13, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px' }}>
                    <strong>Reason:</strong> {ex.reviewer_comment}
                  </div>
                )}

                {/* Employer reject form */}
                {isEmployer && rejectingId === ex.id && (
                  <div style={{ marginTop: 10 }}>
                    <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} placeholder="Reason for rejection (optional but recommended)…"
                      style={{ width: '100%', padding: '8px 12px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 13, color: t.inputText, fontFamily: 'Inter,system-ui', boxSizing: 'border-box', resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => { setRejectingId(null); setRejectReason('') }} style={{ padding: '6px 14px', background: t.input, color: t.text, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                      <button onClick={() => confirmReject(ex)} style={{ padding: '6px 14px', background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Confirm reject</button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
